import { useContext, useMemo, useState } from 'react'
import { Box, Tab, Tabs } from '@mui/material'
import InsightsIcon from '@mui/icons-material/Insights'
import { MentorClinicsContext } from 'src/mentor/layout'
import PractitionerReportInsightsPanel from './PractitionerReportInsightsPanel'

function ComingSoon() {
  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        gap: 1.5,
        px: 3,
        borderRadius: '12px',
        border: '1px dashed rgba(16, 185, 129, 0.3)',
        backgroundColor: 'rgba(16, 185, 129, 0.04)',
      }}
    >
      <InsightsIcon sx={{ fontSize: 48, color: 'rgba(16, 185, 129, 0.55)' }} />
      <Box sx={{ fontSize: '18px', fontWeight: 700, color: 'rgba(15, 23, 42, 0.85)' }}>
        Stats are coming to your dashboard
      </Box>
      <Box sx={{ fontSize: '14px', color: 'rgba(71, 85, 105, 0.85)', maxWidth: 460 }}>
        Detailed statistics will appear here for your role.
      </Box>
    </Box>
  )
}

// Height of the owner/practitioner entry-tab bar — used to stack the My-Reports sticky sub-tabs
// directly beneath it when both are shown.
const ENTRY_TABS_HEIGHT = 53

/**
 * Stats page content. Clinic owners see the clinic-wide volume stats (passed in via {@code ownerStats}
 * so this stays agnostic to which owner-stats component the page uses); practitioners see "My Reports"
 * insights for their own reports. A practitioner who is also an owner gets a top-level toggle between
 * the two.
 *
 * Scrolling is deliberately FLAT: this view owns no scroll region of its own — everything flows in the
 * page's single (window) scroll, exactly like the other tabs. The only pinned elements are the
 * entry-point tabs and each sub-view's own sticky bar, anchored just below the fixed page header via
 * {@code stickyTop} so there's never a scrollbar-within-a-scrollbar.
 */
export default function ClinicStatsView({
  allRoles,
  ownerStats,
  clinicBrandId,
  clinicLocationId,
  mentorProfileData,
  stickyTop = 0,
}) {
  const isOwner = Boolean(allRoles?.isClinicOwner)
  const isPractitioner = Boolean(allRoles?.isDoctor || allRoles?.isTherapist || allRoles?.isScreener)

  // The owner "Clinic Stats" view (ClinicReportsWithVisualizations) needs the mentor's associated
  // clinic brands — without them it only renders "No clinic brand available". Some accounts carry
  // ROLE_CLINIC_OWNER without any associated brands in this flow, so only surface the Clinic Stats
  // tab when brands are present (or still loading); otherwise practitioners land straight on
  // "My Reports" instead of an empty-brand error.
  const clinicsContext = useContext(MentorClinicsContext) || {}
  const hasClinicBrands = (clinicsContext?.associatedClinicBrands?.length || 0) > 0
  const showClinicStats = isOwner && (hasClinicBrands || Boolean(clinicsContext?.loadingAssociationsData))

  const views = useMemo(() => {
    const v = []
    if (showClinicStats) v.push('clinic')
    if (isPractitioner) v.push('myReports')
    return v
  }, [showClinicStats, isPractitioner])

  // Null until the user explicitly picks a tab. Default preference is owner Clinic Stats (once brands
  // resolve) so owners land on their clinic dashboard; brand-less practitioners default to My Reports.
  const [userSelectedView, setUserSelectedView] = useState(null)

  if (views.length === 0) {
    return <ComingSoon />
  }

  const preferredView = showClinicStats ? 'clinic' : 'myReports'
  const currentView =
    userSelectedView && views.includes(userSelectedView)
      ? userSelectedView
      : views.includes(preferredView)
        ? preferredView
        : views[0]

  // When both views are present the entry tabs occupy a sticky strip, so the My-Reports sub-tabs
  // must pin just beneath them; otherwise they pin directly under the page header.
  const showEntryTabs = views.length > 1
  const myReportsStickyTop = showEntryTabs ? stickyTop + ENTRY_TABS_HEIGHT : stickyTop

  // Both views flow in the page's single scroll — no inner scroll region.
  const content = (
    <>
      {showClinicStats ? (
        <Box sx={{ display: currentView === 'clinic' ? 'block' : 'none' }}>{ownerStats}</Box>
      ) : null}
      {isPractitioner ? (
        <Box sx={{ display: currentView === 'myReports' ? 'block' : 'none' }}>
          <PractitionerReportInsightsPanel
            clinicBrandId={clinicBrandId}
            clinicLocationId={clinicLocationId}
            mentorProfileData={mentorProfileData}
            stickyTop={myReportsStickyTop}
          />
        </Box>
      ) : null}
    </>
  )

  // Single role/owner: content flows straight into the page scroll.
  if (!showEntryTabs) {
    return <Box sx={{ width: '100%' }}>{content}</Box>
  }

  // Owner + practitioner: entry-point tabs pinned under the page header; content flows below in the
  // same page scroll (no nested scrollbar).
  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          position: 'sticky',
          top: `${stickyTop}px`,
          zIndex: 6,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Tabs
          value={currentView}
          onChange={(e, v) => setUserSelectedView(v)}
          sx={{
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, fontSize: '0.95rem', minHeight: 52 },
            '& .Mui-selected': { color: '#7450AA' },
            '& .MuiTabs-indicator': { backgroundColor: '#7450AA', height: 3 },
          }}
        >
          <Tab value="clinic" label="Clinic Stats" />
          <Tab value="myReports" label="My Reports" />
        </Tabs>
      </Box>
      {content}
    </Box>
  )
}
